import { useEffect, useState } from 'react';

// Measures how much of the page footer is currently visible in the viewport
// and returns that number of pixels. Components that render fixed-position
// readers (blog modal, view-code modal, etc.) can subtract this value from
// their bottom edge so the reader's frame never overlaps the footer as the
// user scrolls down.
//
// Returns { footerOverlap, topOffset, maxHeight } where:
//   footerOverlap — px of footer currently visible from the bottom of the viewport
//   topOffset     — default top gap for the reader (matches header)
//   maxHeight     — `calc(100vh - topOffset - footerOverlap - gap)` as a string
export default function useBottomBoundedViewport({ topOffset = 80, bottomGap = 16, footerSelector = '.footer' } = {}) {
  const [footerOverlap, setFooterOverlap] = useState(0);

  useEffect(() => {
    const compute = () => {
      const footer = document.querySelector(footerSelector);
      if (!footer) { setFooterOverlap(0); return; }
      const rect = footer.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // rect.top is where the footer starts in viewport space.
      // If the footer's top is BELOW the viewport bottom, there's no overlap.
      // Otherwise the overlap equals (vh - rect.top).
      const overlap = Math.max(0, vh - rect.top);
      setFooterOverlap(overlap);
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    // also recompute after layout settles
    const raf = requestAnimationFrame(compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
      cancelAnimationFrame(raf);
    };
  }, [footerSelector]);

  return {
    footerOverlap,
    topOffset,
    bottomGap,
    maxHeight: `calc(100vh - ${topOffset}px - ${footerOverlap + bottomGap}px)`,
    bottom: `${footerOverlap + bottomGap}px`
  };
}
